import { lazy } from 'react'
import { Route } from 'react-router-dom'
import HomePageLayout from '@/layout/home/HomePageLayout'
import RoleRoutes from '@/routes/RoleRoutes'
import { ROLES } from '@/config/roles'

const PRODUCTION_TEST_ROUTE_HOSTS = new Set([
  'digipalz.io',
  'www.digipalz.io',
  'digipalz.ca',
  'www.digipalz.ca'
])

export const areTestRoutesEnabled = () => {
  if (import.meta.env.MODE === 'production') return false
  if (typeof window !== 'undefined') {
    const host = window.location.hostname
    if (PRODUCTION_TEST_ROUTE_HOSTS.has(host)) return false
  }
  return (
    import.meta.env.MODE === 'staging' ||
    import.meta.env.MODE === 'development'
  )
}

const noop = () => {}

// Other test pages
const MissionRewardsSummaryModalTest = lazy(
  () => import('@/pages/game/test/MissionRewardsSummaryModalTest')
)
const MissionEndingTestPage = lazy(
  () => import('@/pages/game/test/MissionEndingTest')
)
const MissionLoadingTestPage = lazy(
  () => import('@/pages/game/test/MissionLoadingTest')
)
const ParentPlanOptionsPage = lazy(
  () => import('@/pages/plan-options/ParentPlanOptionsPage')
)
const LeaderboardTestPage = lazy(
  () => import('@/pages/dashboard/student/LeaderboardTestPage')
)
const StudentDashboardLayout = lazy(
  () => import('@/layout/dashboard/student/StudentDashboardLayout')
)
const Educator = lazy(() => import('@/pages/home/educator/Educator'))
const EmailVerificationOtpTestPage = lazy(
  () => import('@/pages/auth/verify-email/EmailVerificationOtpTest')
)
const ForgotPasswordFlowTestPage = lazy(
  () => import('@/pages/auth/login/ForgotPasswordFlowTest')
)
const LoginTestPage = lazy(() => import('@/pages/auth/login/LoginTestPage'))

const SchoolAdminTestIndex = lazy(
  () => import('@/pages/test/school-admin/SchoolAdminTestIndex')
)
const SchoolAdminRegisterTestPage = lazy(
  () => import('@/pages/test/school-admin/RegisterTestPage')
)
const SchoolAdminVerifyEmailTestPage = lazy(
  () => import('@/pages/test/school-admin/VerifyEmailTestPage')
)
const SchoolAdminAccountSetupTestPage = lazy(
  () => import('@/pages/test/school-admin/AccountSetupTestPage')
)
const SchoolAdminPlanOptionsTestPage = lazy(
  () => import('@/pages/test/school-admin/PlanOptionsTestPage')
)
const SchoolAdminPaymentSuccessTestPage = lazy(
  () => import('@/pages/test/school-admin/PaymentSuccessTestPage')
)
const SchoolAdminOnboardingDetailsTestPage = lazy(
  () => import('@/pages/test/school-admin/OnboardingSchoolDetailsTestPage')
)
const SchoolAdminOnboardingInviteTestPage = lazy(() =>
  import('@/pages/test/school-admin/UserManagementTestPages').then((m) => ({
    default: m.OnboardingInviteTeachersTestPage
  }))
)
const SchoolAdminSchoolUsersTestPage = lazy(() =>
  import('@/pages/test/school-admin/UserManagementTestPages').then((m) => ({
    default: m.SchoolUsersTestPage
  }))
)
const SchoolAdminTeacherInviteTestPage = lazy(
  () => import('@/pages/test/school-admin/TeacherInviteAcceptTestPage')
)
const SchoolAdminDashboardTestPage = lazy(
  () => import('@/pages/test/school-admin/DashboardTestPage')
)
const BoardDashboardTestPage = lazy(
  () => import('@/pages/test/board/BoardDashboardTestPage')
)
const RegisterMobileTestPage = lazy(
  () => import('@/pages/test/register/RegisterMobileTestPage')
)
const SchoolAdminSchoolDetailsTestPage = lazy(
  () => import('@/pages/test/school-admin/SchoolDetailsDashboardTestPage')
)
const SchoolAdminClassroomsTestPage = lazy(
  () => import('@/pages/test/school-admin/ClassroomsTestPage')
)
const SchoolAdminLessonsTestPage = lazy(
  () => import('@/pages/test/school-admin/LessonsTestPage')
)
const SchoolAdminProfileTestPage = lazy(
  () => import('@/pages/test/school-admin/ProfileTestPage')
)
const SchoolAdminBillingTestPage = lazy(
  () => import('@/pages/test/school-admin/BillingTestPage')
)
const SharedContactTestPage = lazy(
  () => import('@/pages/test/shared/SharedContactTestPage')
)
const SharedHelpTestPage = lazy(
  () => import('@/pages/test/shared/SharedHelpTestPage')
)

// Games
const FlipCardGame = lazy(
  () => import('@/components/game/mini_game/flip_card/FlipCardGame')
)
const ConsentGame = lazy(
  () => import('@/components/game/mini_game/consent_game/ConsentGame')
)
const AnimalZooGame = lazy(
  () => import('@/pages/game/miniGame/animalZoo/AnimalZooGame')
)
const CarRaceGame = lazy(() => import('@/pages/game/miniGame/carRace/carTest'))
const ChatModeratorGame = lazy(
  () => import('@/pages/game/miniGame/chatModerator/ChatModerator')
)
const PacmanGame = lazy(() => import('@/pages/game/miniGame/pacman/Pacman'))
const SymtomSorter = lazy(
  () => import('@/pages/game/miniGame/symtomSorter/SymtomSorter')
)
const HowYouReact = lazy(
  () => import('@/pages/game/miniGame/houYouReact/HowYouReact')
)
const AuthenticationFactorGame = lazy(
  () => import('@/pages/game/miniGame/mfa/MultiFactorAuthentication')
)
const DigitalFootprint = lazy(
  () => import('@/pages/game/miniGame/digitalFootprint/DigitalFootprint')
)
const IpAddressGame = lazy(
  () => import('@/pages/game/miniGame/ipAddress/IpAddressGame')
)
const OutSmart = lazy(() => import('@/pages/game/miniGame/outSmart/OutSmart'))
const GhostHuntPage = lazy(
  () => import('@/pages/game/miniGame/ghostHunt/GhostHuntPage')
)
const PasswordInspector = lazy(
  () => import('@/pages/game/miniGame/passwordInsperctor/PasswordInspector')
)

// Actions
const SpiderMove = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/spiderMove/SpiderMove'
    )
)
const CyberDefinition = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/cyberDefinition/CyberDefinition'
    )
)
const TrustedAdults = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/trustedAdults/TrustedAdults'
    )
)
const BackpackActionComponent = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/backpack/BackpackActionComponent'
    )
)
const FootprintVideo = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/footprintVideo/FootprintVideo'
    )
)
const LunaPost = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/lunaPost/LunaPost')
)
const MiloPost = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/miloPost/MiloPost')
)
const PostBook = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/postBook/PostBook')
)
const Tshirt = lazy(
  () => import('@/components/game/mission/scene_cmp/action_cmp/tshirt/Tshirt')
)
const PuzzleDrop = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/puzzleDrop/PuzzleDrop'
    )
)
const PlushToy = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/plushToy/PlushToy')
)
const SmartGoggle = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/smartGoogle/SmartGoggle'
    )
)
const IpAddressScrap = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/ipAddressScrap/IpAddressScrap'
    )
)
const SearchWarrant = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/searchWarrant/SearchWarrant'
    )
)
const PhoneCall = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/phoneCall/PhoneCall')
)
const ReceivePhoneCall = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/receivePhoneCall/ReceivePhoneCall'
    )
)
const SnappyPal = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/snappyPal/SnappyPal')
)
const SnappyPalMap = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/snappyPal/SnappyPalMap'
    )
)
const ApplyUni = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/applyUni/ApplyUni')
)
const ImagePopUp = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/imagePopUp/ImagePopUp'
    )
)
const SavePasswordAction = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/savePasssword/SavePasswordAction'
    )
)
const ImageSoundAction = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/imageSound/ImageSoundAction'
    )
)
const PhoneCode = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/phoneCode/PhoneCode')
)
const ThumbScan = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/thumbScan/ThumbScan')
)
const SpeakerVolume = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/speakerVolume/SpeakerVolume'
    )
)
const WhatYouSee = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/whatYouSee/WhatYouSee'
    )
)
const DangerAlert = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/dangerAlert/DangerAlert'
    )
)
const PhoneMessage = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/phoneMessage/PhoneMessage'
    )
)
const BookPage = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/bookPage/BookPage')
)
const Paparazzi = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/paparazzi/Paparazzi')
)
const ShouldYouPost = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/shouldYouPost/ShouldYouPost'
    )
)
const LetterPopup = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/letterPopup/LetterPopup'
    )
)
const PlayNarration = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/playNarration/PlayNarration'
    )
)
const PlayOnlyNarration = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/playOnlyNarration/PlayOnlyNarration'
    )
)
const BugScream = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/bugScream/BugScream'
    )
)
const AstraBackpack = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/astraBackpack/AstraBackpack'
    )
)
const WebCam = lazy(
  () => import('@/components/game/mission/scene_cmp/action_cmp/webcam/WebCam')
)
const NotPutOnInternet = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/notPutOnInternet/NotPutOnInternet'
    )
)
const SceneIntro = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/scene_intro/SceneIntro'
    )
)
const EmptyAction = lazy(
  () =>
    import(
      '@/components/game/mission/scene_cmp/action_cmp/emptyAction/EmptyAction'
    )
)

export const renderTestRoutes = () => {
  if (!areTestRoutesEnabled()) return null

  return (
    <Route element={<RoleRoutes allowedRoles={[ROLES.ADMIN]} />}>
      {/* Other test pages */}
      <Route
        path='/test/mission-rewards-summary'
        element={<MissionRewardsSummaryModalTest />}
      />
      <Route path='/test/mission-ending' element={<MissionEndingTestPage />} />
      <Route
        path='/test/mission-loading'
        element={<MissionLoadingTestPage />}
      />
      <Route
        path='/test/parent-plan-options'
        element={<ParentPlanOptionsPage />}
      />
      <Route element={<HomePageLayout />}>
        <Route path='/test/educator-lp' element={<Educator />} />
      </Route>
      <Route
        path='/test/email-verification-otp'
        element={<EmailVerificationOtpTestPage />}
      />
      <Route
        path='/test/forgot-password'
        element={<ForgotPasswordFlowTestPage />}
      />
      <Route path='/test/login' element={<LoginTestPage />} />

      {/* School admin UI previews (Digipalz admin only) */}
      <Route path='/test/school-admin' element={<SchoolAdminTestIndex />} />
      <Route
        path='/test/school-admin/register'
        element={<SchoolAdminRegisterTestPage />}
      />
      <Route
        path='/test/school-admin/verify-email'
        element={<SchoolAdminVerifyEmailTestPage />}
      />
      <Route
        path='/test/school-admin/account-setup'
        element={<SchoolAdminAccountSetupTestPage />}
      />
      <Route
        path='/test/school-admin/plan-options'
        element={<SchoolAdminPlanOptionsTestPage />}
      />
      <Route
        path='/test/school-admin/payment-success'
        element={<SchoolAdminPaymentSuccessTestPage />}
      />
      <Route
        path='/test/school-admin/onboarding-school-details'
        element={<SchoolAdminOnboardingDetailsTestPage />}
      />
      <Route
        path='/test/school-admin/onboarding-invite-teachers'
        element={<SchoolAdminOnboardingInviteTestPage />}
      />
      <Route
        path='/test/school-admin/teacher-invite-accept'
        element={<SchoolAdminTeacherInviteTestPage />}
      />
      <Route
        path='/test/school-admin/school-users'
        element={<SchoolAdminSchoolUsersTestPage />}
      />
      <Route
        path='/test/school-admin/dashboard'
        element={<SchoolAdminDashboardTestPage />}
      />
      <Route path='/test/register' element={<RegisterMobileTestPage />} />
      <Route path='/test/board' element={<BoardDashboardTestPage />} />
      <Route path='/test/board/*' element={<BoardDashboardTestPage />} />
      <Route
        path='/test/school-admin/school-details'
        element={<SchoolAdminSchoolDetailsTestPage />}
      />
      <Route
        path='/test/school-admin/classrooms'
        element={<SchoolAdminClassroomsTestPage />}
      />
      <Route
        path='/test/school-admin/lessons'
        element={<SchoolAdminLessonsTestPage />}
      />
      <Route
        path='/test/school-admin/profile'
        element={<SchoolAdminProfileTestPage />}
      />
      <Route
        path='/test/school-admin/billing'
        element={<SchoolAdminBillingTestPage />}
      />
      <Route path='/test/shared/contact' element={<SharedContactTestPage />} />
      <Route path='/test/shared/help' element={<SharedHelpTestPage />} />

      {/* Games */}
      <Route
        path='/test/flip-card-game'
        element={<FlipCardGame onComplete={noop} />}
      />
      <Route
        path='/test/consent-game'
        element={<ConsentGame onComplete={noop} />}
      />
      <Route
        path='/test/animal-zoo-game'
        element={<AnimalZooGame onComplete={noop} />}
      />
      <Route
        path='/test/car-race-game'
        element={<CarRaceGame onComplete={noop} />}
      />
      <Route
        path='/test/chat-moderator-game'
        element={<ChatModeratorGame onComplete={noop} />}
      />
      <Route
        path='/test/pacman-game'
        element={<PacmanGame onComplete={noop} />}
      />
      <Route
        path='/test/symptom-sorter'
        element={<SymtomSorter onComplete={noop} />}
      />
      <Route
        path='/test/how-you-react'
        element={<HowYouReact onComplete={noop} />}
      />
      <Route
        path='/test/mfa'
        element={<AuthenticationFactorGame onComplete={noop} />}
      />
      <Route
        path='/test/digital-footprint'
        element={<DigitalFootprint onComplete={noop} />}
      />
      <Route
        path='/test/ip-address-game'
        element={<IpAddressGame onComplete={noop} />}
      />
      <Route path='/test/out-smart' element={<OutSmart onComplete={noop} />} />
      <Route
        path='/test/ghost-hunt'
        element={<GhostHuntPage onComplete={noop} />}
      />
      <Route
        path='/test/password-inspector'
        element={<PasswordInspector onComplete={noop} />}
      />

      {/* Actions */}
      <Route
        path='/test/spider-move'
        element={<SpiderMove onComplete={noop} data={{}} />}
      />
      <Route
        path='/test/cyber-definition'
        element={<CyberDefinition onComplete={noop} />}
      />
      <Route
        path='/test/trusted-adults'
        element={<TrustedAdults onComplete={noop} />}
      />
      <Route
        path='/test/backpack'
        element={<BackpackActionComponent onComplete={noop} />}
      />
      <Route
        path='/test/footprint-video'
        element={<FootprintVideo onComplete={noop} />}
      />
      <Route path='/test/luna-post' element={<LunaPost onComplete={noop} />} />
      <Route path='/test/milo-post' element={<MiloPost onComplete={noop} />} />
      <Route path='/test/post-book' element={<PostBook onComplete={noop} />} />
      <Route path='/test/tshirt' element={<Tshirt onComplete={noop} />} />
      <Route
        path='/test/puzzle-drop'
        element={<PuzzleDrop onComplete={noop} />}
      />
      <Route path='/test/plush-toy' element={<PlushToy onComplete={noop} />} />
      <Route
        path='/test/smart-goggle'
        element={<SmartGoggle onComplete={noop} />}
      />
      <Route
        path='/test/ip-address-scrap'
        element={<IpAddressScrap onComplete={noop} />}
      />
      <Route
        path='/test/search-warrant'
        element={<SearchWarrant onComplete={noop} />}
      />
      <Route
        path='/test/phone-call'
        element={<PhoneCall onComplete={noop} data={{}} />}
      />
      <Route
        path='/test/receive-phone-call'
        element={<ReceivePhoneCall onComplete={noop} data={{}} />}
      />
      <Route path='/test/snappy-pal' element={<SnappyPal onComplete={noop} />} />
      <Route
        path='/test/snappy-pal-map'
        element={<SnappyPalMap onComplete={noop} />}
      />
      <Route path='/test/apply-uni' element={<ApplyUni onComplete={noop} />} />
      <Route
        path='/test/image-popup'
        element={<ImagePopUp onComplete={noop} data={{}} />}
      />
      <Route
        path='/test/save-password'
        element={<SavePasswordAction onComplete={noop} />}
      />
      <Route
        path='/test/image-sound'
        element={<ImageSoundAction onComplete={noop} data={{}} />}
      />
      <Route path='/test/phone-code' element={<PhoneCode onComplete={noop} />} />
      <Route path='/test/thumb-scan' element={<ThumbScan onComplete={noop} />} />
      <Route
        path='/test/speaker-volume'
        element={<SpeakerVolume onComplete={noop} data={{}} />}
      />
      <Route
        path='/test/what-you-see'
        element={<WhatYouSee onComplete={noop} />}
      />
      <Route
        path='/test/danger-alert'
        element={<DangerAlert onComplete={noop} />}
      />
      <Route
        path='/test/phone-message'
        element={<PhoneMessage onComplete={noop} data={{}} />}
      />
      <Route
        path='/test/book-page'
        element={<BookPage onComplete={noop} data={{}} />}
      />
      <Route path='/test/paparazzi' element={<Paparazzi onComplete={noop} />} />
      <Route
        path='/test/should-you-post'
        element={<ShouldYouPost onComplete={noop} />}
      />
      <Route
        path='/test/letter-popup'
        element={<LetterPopup onComplete={noop} />}
      />
      <Route
        path='/test/play-narration'
        element={<PlayNarration onComplete={noop} data={{}} />}
      />
      <Route
        path='/test/play-only-narration'
        element={<PlayOnlyNarration onComplete={noop} data={{}} />}
      />
      <Route
        path='/test/bug-scream'
        element={<BugScream onComplete={noop} />}
      />
      <Route
        path='/test/astra-backpack'
        element={<AstraBackpack onComplete={noop} />}
      />
      <Route path='/test/webcam' element={<WebCam onComplete={noop} />} />
      <Route
        path='/test/not-put-on-internet'
        element={<NotPutOnInternet onComplete={noop} />}
      />
      <Route
        path='/test/scene-intro'
        element={<SceneIntro onComplete={noop} data={{}} />}
      />
      <Route
        path='/test/empty-action'
        element={<EmptyAction onComplete={noop} data={{}} />}
      />
    </Route>
  )
}

export const renderStudentDashboardTestRoutes = () => {
  if (!areTestRoutesEnabled()) return null

  return (
    <Route element={<RoleRoutes allowedRoles={[ROLES.ADMIN]} />}>
      <Route element={<StudentDashboardLayout />}>
        <Route
          path='/test/student-leaderboard'
          element={<LeaderboardTestPage />}
        />
      </Route>
    </Route>
  )
}
